import React from "react";
import { TripTypeSelectorProps } from "./types";
import { useLoyaltySession } from "../../hooks/useLoyaltySession";
import { formatMiles } from "../../services/mockLoyalty";

const TripTypeSelector: React.FC<TripTypeSelectorProps> = ({
    tripType,
    setTripType,
    bookWithMiles,
    setBookWithMiles,
}) => {
    // MOVMO-541: once the loyalty session is active, the toggle's label
    // surfaces the member's real miles balance instead of prompting sign-in.
    // The toggle itself stays fully functional either way.
    const { sessionActive, link } = useLoyaltySession();
    const milesLabel =
        sessionActive && link
            ? `Book with Mabuhay Miles · ${formatMiles(link.milesBalance)} miles`
            : "Sign in to book with miles";

    return (
        <>
            {/* Desktop Layout - Also shown in landscape mobile */}
            <div className="hidden lg:flex landscape:flex items-center justify-between mb-6">
                <div className="flex gap-8">
                    <label className="flex items-center cursor-pointer group">
                        <div className="relative">
                            <input
                                type="radio"
                                name="tripType"
                                value="round-trip"
                                checked={tripType === "round-trip"}
                                onChange={() => setTripType("round-trip")}
                                className="sr-only"
                            />
                            <div
                                className={`w-5 h-5 rounded-full border-2 ${
                                    tripType === "round-trip"
                                        ? "bg-blue-600 border-blue-600"
                                        : "border-gray-400 group-hover:border-gray-500"
                                }`}
                            >
                                {tripType === "round-trip" && (
                                    <div className="w-2 h-2 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                                )}
                            </div>
                        </div>
                        <span className="ml-3 text-gray-700 text-base">
                            Round trip
                        </span>
                    </label>

                    <label className="flex items-center cursor-pointer group">
                        <div className="relative">
                            <input
                                type="radio"
                                name="tripType"
                                value="one-way"
                                checked={tripType === "one-way"}
                                onChange={() => setTripType("one-way")}
                                className="sr-only"
                            />
                            <div
                                className={`w-5 h-5 rounded-full border-2 ${
                                    tripType === "one-way"
                                        ? "bg-blue-600 border-blue-600"
                                        : "border-gray-400 group-hover:border-gray-500"
                                }`}
                            >
                                {tripType === "one-way" && (
                                    <div className="w-2 h-2 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                                )}
                            </div>
                        </div>
                        <span className="ml-3 text-gray-700 text-base">
                            One way
                        </span>
                    </label>

                    <label className="flex items-center cursor-pointer group">
                        <div className="relative">
                            <input
                                type="radio"
                                name="tripType"
                                value="multi-city"
                                checked={tripType === "multi-city"}
                                onChange={() => setTripType("multi-city")}
                                className="sr-only"
                            />
                            <div
                                className={`w-5 h-5 rounded-full border-2 ${
                                    tripType === "multi-city"
                                        ? "bg-blue-600 border-blue-600"
                                        : "border-gray-400 group-hover:border-gray-500"
                                }`}
                            >
                                {tripType === "multi-city" && (
                                    <div className="w-2 h-2 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                                )}
                            </div>
                        </div>
                        <span className="ml-3 text-gray-700 text-base">
                            Multi city
                        </span>
                    </label>
                </div>

                <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                        <button
                            type="button"
                            onClick={() => setBookWithMiles(!bookWithMiles)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                bookWithMiles ? "bg-blue-600" : "bg-gray-300"
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    bookWithMiles
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                }`}
                            />
                        </button>
                        <span className="ml-3 text-gray-700 text-base">
                            {milesLabel}
                        </span>
                    </label>
                </div>
            </div>

            {/* Mobile Layout - Hidden in landscape */}
            <div className="lg:hidden landscape:hidden mb-6">
                {/* Trip Type Radio Buttons */}
                <div className="flex gap-6 mb-4">
                    <label className="flex items-center cursor-pointer group">
                        <div className="relative">
                            <input
                                type="radio"
                                name="tripType-mobile"
                                value="round-trip"
                                checked={tripType === "round-trip"}
                                onChange={() => setTripType("round-trip")}
                                className="sr-only"
                            />
                            <div
                                className={`w-5 h-5 rounded-full border-2 ${
                                    tripType === "round-trip"
                                        ? "border-blue-600"
                                        : "border-gray-400 group-hover:border-gray-500"
                                }`}
                                style={
                                    tripType === "round-trip"
                                        ? { backgroundColor: "#0047ba" }
                                        : {}
                                }
                            >
                                {tripType === "round-trip" && (
                                    <div className="w-2 h-2 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                                )}
                            </div>
                        </div>
                        <span className="ml-2 text-gray-700 text-base">
                            Round trip
                        </span>
                    </label>

                    <label className="flex items-center cursor-pointer group">
                        <div className="relative">
                            <input
                                type="radio"
                                name="tripType-mobile"
                                value="one-way"
                                checked={tripType === "one-way"}
                                onChange={() => setTripType("one-way")}
                                className="sr-only"
                            />
                            <div
                                className={`w-5 h-5 rounded-full border-2 ${
                                    tripType === "one-way"
                                        ? "border-blue-600"
                                        : "border-gray-400 group-hover:border-gray-500"
                                }`}
                                style={
                                    tripType === "one-way"
                                        ? { backgroundColor: "#0047ba" }
                                        : {}
                                }
                            >
                                {tripType === "one-way" && (
                                    <div className="w-2 h-2 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                                )}
                            </div>
                        </div>
                        <span className="ml-2 text-gray-700 text-base">
                            One way
                        </span>
                    </label>
                </div>

                {/* Second row with Multi city */}
                <div className="flex gap-6 mb-4">
                    <label className="flex items-center cursor-pointer group">
                        <div className="relative">
                            <input
                                type="radio"
                                name="tripType-mobile"
                                value="multi-city"
                                checked={tripType === "multi-city"}
                                onChange={() => setTripType("multi-city")}
                                className="sr-only"
                            />
                            <div
                                className={`w-5 h-5 rounded-full border-2 ${
                                    tripType === "multi-city"
                                        ? "border-blue-600"
                                        : "border-gray-400 group-hover:border-gray-500"
                                }`}
                                style={
                                    tripType === "multi-city"
                                        ? { backgroundColor: "#0047ba" }
                                        : {}
                                }
                            >
                                {tripType === "multi-city" && (
                                    <div className="w-2 h-2 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                                )}
                            </div>
                        </div>
                        <span className="ml-2 text-gray-700 text-base">
                            Multi city
                        </span>
                    </label>
                </div>

                {/* Book with Miles Toggle */}
                <div className="flex items-center justify-center">
                    <label className="flex items-center cursor-pointer">
                        <button
                            type="button"
                            onClick={() => setBookWithMiles(!bookWithMiles)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                bookWithMiles ? "bg-gray-300" : "bg-gray-300"
                            }`}
                            style={
                                bookWithMiles
                                    ? { backgroundColor: "#0047ba" }
                                    : {}
                            }
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    bookWithMiles
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                }`}
                            />
                        </button>
                        <span className="ml-3 text-gray-700 text-base">
                            {milesLabel}
                        </span>
                    </label>
                </div>
            </div>
        </>
    );
};

export default TripTypeSelector;
